'use client';

import clsx from 'clsx';
import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { proxy, useSnapshot } from 'valtio';

declare global {
  interface Window {
    squadup?: {
      title?: string;
      root?: string;
      userId?: number[];
      image?: string;
      eventId?: string;
      [key: string]: any;
    };
    dataLayer?: Object[];
  }
}

export const squadUpState = proxy<{ eventId?: string; eventData?: any }>({
  eventId: undefined,
  eventData: undefined,
});

export function SquadUp() {
  const { eventId, eventData } = useSnapshot(squadUpState);
  const i = useRef(0);

  useEffect(() => {
    // Listen for SquadUp's documented DOM events
    const handleShoppingCartUpdated = (event: Event) => {
      // console.log('shoppingCartUpdated event received', event);

      if (event instanceof CustomEvent && event.detail && window.dataLayer) {
        const cartData = event.detail;
        // console.log('Shopping cart data:', cartData);

        // Only proceed if we have valid cart data
        if (cartData && cartData.items && Array.isArray(cartData.items)) {
          try {
            // Transform cart items to GTM format
            const items = cartData.items.map((item: any, index: number) => {
              // Extract ticket data
              const ticket = item.tickets?.[0] || {};
              const eventInfo = item.event || {};

              return {
                index,
                item_id: ticket.id || eventInfo.id || '',
                item_brand: ticket.type || 'General Admission',
                item_name:
                  eventInfo.name ||
                  `${eventData?.homeTeam?.name || ''} vs ${eventData?.awayTeam?.name || ''}`,
                event_date: eventData?.date || '',
                quantity: ticket.quantity || 1,
                ticket_cost: item.price || 0,
                ticket_final_cost: (item.price || 0) * (ticket.quantity || 1),
                coupon: cartData.promoCode || '',
              };
            });

            // Create and push dataLayer event
            const dataLayerEvent = {
              event: 'add_to_cart',
              value: cartData.total || 0,
              tax: cartData.tax || 0,
              regular_price: cartData.subtotal || 0,
              transaction_id: cartData.id || '',
              ecommerce: {
                items: items,
              },
            };

            // Log the event data for testing
            // console.log('GTM dataLayer event:', dataLayerEvent);

            window.dataLayer.push(dataLayerEvent);
          } catch (error) {
            console.error('Error processing cart data:', error);
          }
        }
      }
    };

    // Handle view changes for tracking begin_checkout
    const handleViewChanged = (e: Event) => {
      if (e instanceof CustomEvent && e.detail && window.dataLayer) {
        const viewData = e.detail;
        // console.log('SquadUp view changed:', viewData);

        // Check if the view changed to checkout
        if (
          viewData.currentView === 'checkout' &&
          viewData.previousView === 'cart'
        ) {
          try {
            // Get cart data from SquadUp if available, or use last known cart data
            const cartData = viewData.cartData || {}; // SquadUp might include cart data with the view change

            // Only proceed if we have items to track
            if (cartData.items && Array.isArray(cartData.items)) {
              // Transform cart items to GTM format
              const items = cartData.items.map((item: any, index: number) => {
                // Extract ticket data
                const ticket = item.tickets?.[0] || {};
                const eventInfo = item.event || {};

                return {
                  index,
                  item_id: ticket.id || eventInfo.id || '',
                  item_brand: ticket.type || 'General Admission',
                  item_name:
                    eventInfo.name ||
                    `${eventData?.homeTeam?.name || ''} vs ${eventData?.awayTeam?.name || ''}`,
                  event_date: eventData?.date || '',
                  quantity: ticket.quantity || 1,
                  ticket_cost: item.price || 0,
                  ticket_final_cost: (item.price || 0) * (ticket.quantity || 1),
                  coupon: cartData.promoCode || '',
                };
              });

              // Create and push begin_checkout dataLayer event
              const checkoutEvent = {
                event: 'begin_checkout',
                value: cartData.total || 0,
                tax: cartData.tax || 0,
                regular_price: cartData.subtotal || 0,
                transaction_id: cartData.id || '',
                ecommerce: {
                  items: items,
                },
              };

              // Log the event data for testing
              // console.log('GTM begin_checkout event:', checkoutEvent);

              window.dataLayer.push(checkoutEvent);
            }
          } catch (error) {
            console.error('Error processing begin_checkout event:', error);
          }
        }
      }
    };

    // Define event loaded handler
    const handleEventLoaded = (e: Event) => {
      if (e instanceof CustomEvent) {
        // console.log('SquadUp event loaded:', e.detail);
      }
    };

    // Define order successful handler
    const handleOrderSuccessful = (e: Event) => {
      if (e instanceof CustomEvent && e.detail && window.dataLayer) {
        const orderData = e.detail;
        // console.log('SquadUp order successful event received:', orderData);
        // console.log('Event timestamp:', new Date().toISOString());

        // Add a unique identifier to track if this is a duplicate event
        const eventId = `purchase_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        // console.log('Generated event ID:', eventId);

        try {
          // Process order data for purchase event tracking
          if (orderData.items && Array.isArray(orderData.items)) {
            // Transform order items to GTM format
            const items = orderData.items.map((item: any, index: number) => {
              // Extract ticket data
              const ticket = item.tickets?.[0] || {};
              const eventInfo = item.event || {};

              return {
                index,
                item_id: ticket.id || eventInfo.id || '',
                item_brand: ticket.type || 'General Admission',
                item_name:
                  eventInfo.name ||
                  `${eventData?.homeTeam?.name || ''} vs ${eventData?.awayTeam?.name || ''}`,
                event_date: eventData?.date || '',
                quantity: ticket.quantity || 1,
                ticket_cost: item.price || 0,
                ticket_final_cost: (item.price || 0) * (ticket.quantity || 1),
                coupon: orderData.promoCode || '',
              };
            });

            // Create and push purchase event
            const purchaseEvent = {
              event: 'purchase',
              value: orderData.total || 0,
              tax: orderData.tax || 0,
              regular_price: orderData.subtotal || 0,
              transaction_id: orderData.id || '',
              ecommerce: {
                items: items,
              },
            };

            // Log the event data for testing
            // console.log('GTM purchase event:', purchaseEvent);

            // Add a debounce mechanism to prevent duplicate events
            // Store the last transaction ID and timestamp
            const lastTransactionId = (window as any)
              ._lastPurchaseTransactionId;
            const lastTransactionTime = (window as any)
              ._lastPurchaseTransactionTime;
            const currentTime = Date.now();

            // If this is the same transaction within 5 seconds, skip it
            if (
              lastTransactionId === purchaseEvent.transaction_id &&
              lastTransactionTime &&
              currentTime - lastTransactionTime < 5000
            ) {
              console.log(
                'Skipping duplicate purchase event for transaction:',
                purchaseEvent.transaction_id,
              );
              return;
            }

            // Update the last transaction info
            (window as any)._lastPurchaseTransactionId =
              purchaseEvent.transaction_id;
            (window as any)._lastPurchaseTransactionTime = currentTime;

            window.dataLayer.push(purchaseEvent);
          }
        } catch (error) {
          console.error('Error processing purchase event:', error);
        }
      }
    };

    // Add event listeners for SquadUp events
    document.addEventListener('shoppingCartUpdated', handleShoppingCartUpdated);
    document.addEventListener('viewChanged', handleViewChanged);
    document.addEventListener('eventLoaded', handleEventLoaded);
    document.addEventListener('orderSuccessful', handleOrderSuccessful);

    return () => {
      // Clean up event listeners
      document.removeEventListener(
        'shoppingCartUpdated',
        handleShoppingCartUpdated,
      );
      document.removeEventListener('viewChanged', handleViewChanged);
      document.removeEventListener('eventLoaded', handleEventLoaded);
      document.removeEventListener('orderSuccessful', handleOrderSuccessful);
    };
  }, [eventData]);

  return (
    <>
      <link
        rel="stylesheet"
        href="https://embed.squadup.com/default/css/bootstrap-namespace.min.css"
      />

      <div
        className={clsx(
          'fixed inset-0 z-[9999] flex items-center justify-center p-4',
          !eventId && 'hidden',
        )}
      >
        <div
          className={clsx('absolute inset-0 bg-black/75', !eventId && 'hidden')}
          aria-hidden="true"
          onClick={() => {
            squadUpState.eventId = undefined;
          }}
        />
        <div className="relative h-[90%] w-full max-w-[90%] overflow-auto rounded-lg bg-white p-5 text-black lg:max-w-[800px]">
          <button
            className="absolute right-5 top-5 text-3xl"
            onClick={() => {
              squadUpState.eventId = undefined;
            }}
          >
            ×
          </button>
          <div key={eventId} id="squadup-checkout" />
        </div>
      </div>

      <script
        dangerouslySetInnerHTML={{
          __html: `
          squadup = {
            root: 'squadup-checkout',
            userId: [6730029],
            title: 'AUSL',
            image: '',
            brandingPosition: 'bottom',
            checkoutButtonText: 'BUY NOW',
            ticketGuardianEnabled: false,
            descriptionEnabled: true,
            reservedSeatingEnabled: true,
            paymentPlansEnabled: true,
            shoppingCartEnabled: true
          }
          `,
        }}
      />
      {eventId && (
        <Script
          key={eventId}
          src={`https://embed.squadup.com/main-v2.min.js?cache=${i.current}`}
          onLoad={() => {
            i.current++;
            console.log(`SquadUp script loaded for eventId: ${eventId}`);
            if (window.squadup) {
              window.squadup.eventId = eventId;
              console.log(
                'Updated window.squadup with eventId',
                window.squadup,
              );
            }
            document.dispatchEvent(new CustomEvent('createSquadupEmbed'));
          }}
        />
      )}
    </>
  );
}
